import React, { useState } from 'react';
import { Copy, Download, Mail, QrCode, ShieldCheck } from 'lucide-react';
import { accountAPI } from '../../../../api/client';
import { apiError } from '../../../../lib/host';
import { useT } from '../../../../lib/i18n';
import { usePreferences } from '../../preferences/PreferencesContext';
import { ErrorBanner, Modal, useBasic } from '../../ui';
import PasswordModal from './PasswordModal';
import { Card, ToggleRow } from './SettingsUI';

function BackupCodes({ codes, onDone }) {
  const { t } = useT();
  const text = codes.join('\n');
  const download = () => {
    const url = URL.createObjectURL(new Blob([`TeleCalX backup codes\n\n${text}\n\nEach code works once.`], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'telecalx-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <Modal title={t('Save your backup codes')} onClose={onDone}>
      <p className="st-muted">{t('If you lose your phone, sign in with one of these codes. Each works once. Keep them somewhere safe: they are shown only now.')}</p>
      <div className="st-codes">{codes.map((c) => <code key={c}>{c}</code>)}</div>
      <div className="st-modal-actions">
        <button type="button" className="secondary-action" onClick={() => navigator.clipboard?.writeText(text)}><Copy size={14} />{t('Copy')}</button>
        <button type="button" className="secondary-action" onClick={download}><Download size={14} />{t('Download')}</button>
        <button type="button" className="primary-action" onClick={onDone}>{t("I've saved them")}</button>
      </div>
    </Modal>
  );
}

function AuthenticatorSetup({ setup, onConfirmed, onClose }) {
  const { t } = useT();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const confirm = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await accountAPI.authenticatorConfirm(code);
      onConfirmed(res.data);
    } catch (err) {
      setError(apiError(err));
      setBusy(false);
    }
  };
  return (
    <Modal title={t('Set up the authenticator app')} onClose={onClose}>
      <form className="st-modal-form" onSubmit={confirm}>
        <ol className="st-steps">
          <li>{t('Install Google Authenticator or Microsoft Authenticator on your phone.')}</li>
          <li>{t('In the app, add an account and scan this QR code (or type the key).')}</li>
        </ol>
        <div className="st-qr">
          {setup.qr_svg ? <img src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(setup.qr_svg)}`} alt={t('QR code to scan')} width={170} height={170} /> : <QrCode size={120} />}
          <div><small>{t('Setup key')}</small><code>{setup.secret}</code></div>
        </div>
        <ol className="st-steps" start={3}><li>{t('Enter the 6-digit code the app shows.')}</li></ol>
        <ErrorBanner message={error} />
        <input className="st-code-input" inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="000000" value={code} autoFocus
          aria-label={t('Authenticator code')} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} />
        <div className="st-modal-actions">
          <button type="button" className="secondary-action" onClick={onClose}>{t('Cancel')}</button>
          <button className="primary-action" disabled={busy || code.length !== 6}>{busy ? t('Checking…') : t('Turn on')}</button>
        </div>
      </form>
    </Modal>
  );
}

/** Two-factor sign-in: the authenticator app (recommended, with backup codes) and / or a code by email. */
export default function TwoFactorCard() {
  const { t } = useT();
  const { notify } = useBasic();
  const { twoFactor, setTwoFactor } = usePreferences();
  const [ask, setAsk] = useState(null); // which change needs the password
  const [setup, setSetup] = useState(null);
  const [codes, setCodes] = useState(null);
  const [error, setError] = useState('');
  const tf = twoFactor || { email: false, authenticator: false, backup_codes_left: 0 };

  const actions = {
    authOn: [t('Turn on the authenticator app'), t('Turn on'), async (pw) => { const res = await accountAPI.authenticatorSetup(pw); setSetup(res.data); setAsk(null); }],
    authOff: [t('Turn off the authenticator app'), t('Turn off'), async (pw) => { const res = await accountAPI.authenticatorDisable(pw); setTwoFactor(res.data); setAsk(null); notify(t('The authenticator app is off.')); }],
    emailOn: [t('Turn on sign-in codes by email'), t('Turn on'), async (pw) => { const res = await accountAPI.emailTwoFactor(true, pw); setTwoFactor(res.data); setAsk(null); notify(t('Sign-in codes by email are on.')); }],
    emailOff: [t('Turn off sign-in codes by email'), t('Turn off'), async (pw) => { const res = await accountAPI.emailTwoFactor(false, pw); setTwoFactor(res.data); setAsk(null); notify(t('Sign-in codes by email are off.')); }],
    codes: [t('New backup codes'), t('Create new codes'), async (pw) => { const res = await accountAPI.backupCodes(pw); setTwoFactor(res.data.two_factor); setCodes(res.data.backup_codes); setAsk(null); }],
  };

  return (
    <Card title={t('Two-Factor Authentication (2FA)')} subtitle={t('Add an extra layer of security to your account.')} icon={ShieldCheck} tone="green" className="st-2fa">
      <ErrorBanner message={error} />
      {tf.required_by && <p className="st-note">{t('{company} requires two-factor sign-in for every member.', { company: tf.required_by })}</p>}
      <div className="st-box-rows">
        <ToggleRow icon={QrCode} tone="blue" title={t('Authenticator App (Recommended)')}
          sub={tf.authenticator ? t('On · {n} backup codes left', { n: tf.backup_codes_left }) : t('Use Google Authenticator, Microsoft Authenticator, etc.')}
          checked={tf.authenticator} onChange={(v) => { setError(''); setAsk(v ? 'authOn' : 'authOff'); }} />
        <ToggleRow icon={Mail} tone="purple" title={t('Email Verification')}
          sub={tf.authenticator && tf.email ? t('Used only if the authenticator app is turned off.') : t('Receive verification codes via email.')}
          checked={tf.email} onChange={(v) => { setError(''); setAsk(v ? 'emailOn' : 'emailOff'); }} />
      </div>
      {tf.authenticator && <button type="button" className="link-button st-pad" onClick={() => setAsk('codes')}>{t('Create new backup codes')}</button>}

      {ask && <PasswordModal title={actions[ask][0]} confirmLabel={actions[ask][1]} danger={/Off$/.test(ask)}
        message={t('Enter your password to change two-factor sign-in.')} onConfirm={actions[ask][2]} onClose={() => setAsk(null)} />}
      {setup && <AuthenticatorSetup setup={setup} onClose={() => setSetup(null)}
        onConfirmed={(data) => { setSetup(null); setTwoFactor(data.two_factor); setCodes(data.backup_codes); notify(t('The authenticator app is on.')); }} />}
      {codes && <BackupCodes codes={codes} onDone={() => setCodes(null)} />}
    </Card>
  );
}
